/**
 * DrawingMetadataForm
 *
 * Side panel form that appears after drawing a polygon on the map.
 * Collects name, commodity, producer info, then saves via API.
 */

import { useState, useEffect } from 'react';
import { X, Save, Loader2, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import * as turf from '@turf/turf';
import { polygonsApi, getErrorMessage } from '../../lib/api';

interface DrawingMetadataFormProps {
    geometry: GeoJSON.Geometry;
    onSave: (polygonId: string) => void;
    onCancel: () => void;
}

export default function DrawingMetadataForm({
    geometry,
    onSave,
    onCancel,
}: DrawingMetadataFormProps) {
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [commodity, setCommodity] = useState('cocoa');
    const [producerName, setProducerName] = useState('');
    const [country, setCountry] = useState('');
    const [region, setRegion] = useState('');

    const [geocoding, setGeocoding] = useState(false);

    // Calculate area from drawn geometry
    const area = (() => {
        try {
            const feat = turf.feature(geometry);
            return (turf.area(feat) / 10000).toFixed(2);
        } catch {
            return '—';
        }
    })();

    // Auto-fill country/region from centroid reverse geocoding
    useEffect(() => {
        try {
            const centroid = turf.centroid(turf.feature(geometry));
            const [lon, lat] = centroid.geometry.coordinates;
            setGeocoding(true);
            polygonsApi.reverseGeocode(lat, lon)
                .then((result) => {
                    if (result.country) setCountry(result.country);
                    if (result.region) setRegion(result.region);
                })
                .catch(() => {})
                .finally(() => setGeocoding(false));
        } catch {
            // ignore centroid calculation errors
        }
    }, [geometry]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        setSaving(true);
        try {
            const result = await polygonsApi.create({
                name: name.trim(),
                geometry,
                commodity,
                producer_name: producerName || undefined,
                country: country || undefined,
                region: region || undefined,
            });
            toast.success(`Polígono "${name}" creado (${area} ha)`);
            onSave(result.id);
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al guardar polígono');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-full max-w-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
                <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-600" />
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            Nuevo Polígono
                        </h3>
                        <p className="text-xs text-green-600 font-medium">{area} ha</p>
                    </div>
                </div>
                <button
                    onClick={onCancel}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                    <X className="w-4 h-4 text-gray-500" />
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre *
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ej: Finca La Esperanza - Lote 3"
                        className="input text-sm"
                        autoFocus
                        required
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Commodity
                    </label>
                    <select
                        value={commodity}
                        onChange={(e) => setCommodity(e.target.value)}
                        className="input text-sm"
                    >
                        <option value="cocoa">Cacao</option>
                        <option value="coffee">Café</option>
                        <option value="palm_oil">Aceite de Palma</option>
                        <option value="soy">Soja</option>
                        <option value="timber">Madera</option>
                        <option value="rubber">Caucho</option>
                        <option value="cattle">Ganado</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Productor
                    </label>
                    <input
                        type="text"
                        value={producerName}
                        onChange={(e) => setProducerName(e.target.value)}
                        placeholder="Nombre del productor"
                        className="input text-sm"
                    />
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            País {geocoding && <Loader2 className="inline w-3 h-3 animate-spin text-gray-400" />}
                        </label>
                        <input
                            type="text"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            placeholder={geocoding ? 'Detectando...' : 'Ej: Colombia'}
                            className="input text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Región {geocoding && <Loader2 className="inline w-3 h-3 animate-spin text-gray-400" />}
                        </label>
                        <input
                            type="text"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            placeholder={geocoding ? 'Detectando...' : 'Ej: Antioquia'}
                            className="input text-sm"
                        />
                    </div>
                </div>

                <div className="flex gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn-secondary flex-1 text-sm"
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={saving || !name.trim()}
                        className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Guardar
                    </button>
                </div>
            </form>
        </div>
    );
}
